/*───────────────────────────────────────────────────────────────
 📄 File: SidebarUserMenu.tsx
 🧩 Description:
    User dropdown menu for Sidebar.tsx.
    Displays user info, dashboard edit, profile, appearance,
    license, and logout actions.
────────────────────────────────────────────────────────────────*/

import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, User, Settings, Shield, Info, LogOut } from 'lucide-react';
import { useDashboardEditStore } from '../../store/dashboardEdit';

interface SidebarUserMenuProps {
    show: boolean;
    position: { bottom: number; left: number } | null;
    user: { fullName?: string; email?: string; isPrimaryAdmin?: boolean } | null;
    onNavigate: (path: string) => void;
    onLogout: () => void;
    onOpenAbout?: () => void;
    t: (key: string, fallback?: string) => string;
}

export function SidebarUserMenu({ show, position, user, onNavigate, onLogout, onOpenAbout, t }: SidebarUserMenuProps) {
    return (
        <AnimatePresence>
            {show && position && (
                <motion.div
                    className="fixed w-64 bg-card border border-border rounded-md shadow-lg"
                    style={{ zIndex: 9999, bottom: position.bottom, left: position.left }}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.15 }}
                >
                    {/* User Info */}
                    <div className="p-4 border-b border-border">
                        <div className="font-medium text-foreground">{user?.fullName || user?.email}</div>
                        <div className="text-sm text-muted-foreground">{user?.email}</div>
                    </div>

                    {/* Dashboard Edit */}
                    <div className="p-2 border-b border-border">
                        <button
                            onClick={() => {
                                onNavigate('/dashboard');
                                useDashboardEditStore.getState().setEditMode(true);
                            }}
                            className="w-full text-left px-3 py-2 text-sm text-foreground hover:bg-muted rounded-md flex items-center gap-2"
                        >
                            <LayoutGrid className="w-4 h-4" />
                            <span>{t('dashboard.editDashboard', 'Edit Dashboard')}</span>
                        </button>
                    </div>

                    {/* Actions */}
                    <div className="p-2">
                        <button
                            onClick={() => onNavigate('/user-profile')}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                        >
                            <User className="h-4 w-4 text-muted-foreground" />
                            <span className="text-foreground">Profile</span>
                        </button>

                        <button
                            onClick={() => onNavigate('/settings/appearance')}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                        >
                            <Settings className="h-4 w-4 text-muted-foreground" />
                            <span className="text-foreground">Appearance</span>
                        </button>

                        {user?.isPrimaryAdmin && (
                            <button
                                onClick={() => onNavigate('/admin/licensing')}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                            >
                                <Shield className="h-4 w-4 text-muted-foreground" />
                                <span className="text-foreground">License</span>
                            </button>
                        )}

                        {onOpenAbout && (
                            <button
                                onClick={onOpenAbout}
                                className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-muted/50 transition-colors"
                            >
                                <Info className="h-4 w-4 text-muted-foreground" />
                                <span className="text-foreground">{t('common:about', 'About')}</span>
                            </button>
                        )}

                        <button
                            onClick={onLogout}
                            className="w-full flex items-center gap-3 px-4 py-2 text-left hover:bg-red-500/10 transition-colors group text-red-600 dark:text-red-400"
                        >
                            <LogOut className="h-4 w-4" />
                            <span>Logout</span>
                        </button>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
